
import { ReactNode, useEffect } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { useAuthState } from '@/hooks/useAuthState';
import { AuthLoading } from './AuthLoading';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useLocation } from 'react-router-dom';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { user, loading, setUser } = useAuthState();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log("Setting up auth state listener...");
    console.log("Current location:", location.pathname);
    console.log("Initial auth state:", { user, loading });
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event, session?.user?.id);
      
      if (event === 'SIGNED_OUT') {
        console.log("User signed out");
        setUser(null);
        navigate('/welcome');
        toast({
          title: "Logget ut",
          description: "Du er nå logget ut av systemet.",
        });
      } else if (event === 'SIGNED_IN' && session?.user) {
        console.log("User signed in:", session.user.id);
        setUser(session.user);
        
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
        
        toast({
          title: "Innlogget",
          description: "Du er nå logget inn.",
        });
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        console.log("Session refreshed for user:", session.user.id);
        setUser(session.user);
      }
    });

    // Check initial session
    const checkSession = async () => {
      try {
        console.log("Checking initial session...");
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session check error:', error);
          toast({
            variant: "destructive",
            title: "Feil ved innlasting",
            description: "Kunne ikke sjekke påloggingsstatus.",
          });
          return;
        }
        
        if (session?.user) {
          console.log("Initial session found for user:", session.user.id);
          setUser(session.user);
          if (location.pathname === '/welcome' || location.pathname === '/login') {
            navigate('/dashboard');
          }
        } else {
          console.log("No initial session found");
          if (!location.pathname.startsWith('/welcome') && 
              !location.pathname.startsWith('/login') && 
              !location.pathname.startsWith('/about') && 
              !location.pathname.startsWith('/features')) {
            navigate('/welcome');
          }
        }
      } catch (error) {
        console.error('Error checking session:', error);
        toast({
          variant: "destructive",
          title: "Systemfeil",
          description: "Kunne ikke initialisere systemet. Prøv å laste siden på nytt.",
        });
      }
    };

    checkSession();

    return () => {
      console.log("Cleaning up auth listener");
      subscription.unsubscribe();
    };
  }, [setUser, toast, navigate, location]);

  if (loading) {
    console.log("AuthProvider: Still loading...");
    return <AuthLoading />;
  }

  console.log("AuthProvider: Rendering with user:", user?.id);
  return (
    <AuthContext.Provider value={{ user, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export { useAuth } from '@/contexts/AuthContext';
